import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { PlaylistRoutePaths } from "../playlist/PlaylistRoutePaths";
import {
  CarouselSidebar,
  isSidebarBesideSlides,
  toCarouselSidebar,
} from "./CarouselSidebar";

const param = PlaylistRoutePaths.SIDEBAR_SEARCH_PARAM;

interface SidebarHistoryState {
  sidebarPushed: true;
}

function isSidebarHistoryState(state: unknown): state is SidebarHistoryState {
  return (
    typeof state === "object" &&
    state !== null &&
    "sidebarPushed" in state &&
    state.sidebarPushed === true
  );
}

/**
 * The open sidebar lives in the url, so that browser back closes a sidebar taking the whole screen
 * instead of leaving the carousel. Only opening such a sidebar adds a history entry: beside the
 * slides, switching between sidebars, or showing one initially replaces the current entry.
 */
export function useCarouselSidebar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const openSidebar = toCarouselSidebar(searchParams.get(param));

  function open(sidebar: CarouselSidebar, options?: { replace?: boolean }) {
    if (openSidebar === sidebar) return;
    const push = !options?.replace && !openSidebar && !isSidebarBesideSlides();
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set(param, sidebar);
        return next;
      },
      {
        replace: !push,
        //a replaced entry keeps the marker, so closing still goes back to the entry without sidebar
        state: push ? { sidebarPushed: true } : location.state,
      },
    );
  }

  function close() {
    if (!openSidebar) return;
    if (isSidebarHistoryState(location.state)) {
      navigate(-1);
      return;
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(param);
        return next;
      },
      { replace: true },
    );
  }

  return { openSidebar, open, close };
}
